import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { LanguageService } from '@core/services/language.service';
import { LoadingService } from '@core/services/loading.service';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-dashboard-stories',
  templateUrl: './stories.component.html',
  styleUrls: ['./stories.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class StoriesComponent implements OnInit {
  public stories: any[] = [];
  public filteredStories: any[] = [];
  public searchValue = '';
  public ColumnMode = ColumnMode;
  public itemsPerPage = 10;
  public lang: string = 'ar';

  get datatableMessages(): any {
    return {
      emptyMessage: this.lang === 'ar' ? 'لا توجد بيانات' : 'No data to display',
      totalMessage: this.lang === 'ar' ? 'الإجمالي' : 'total'
    };
  }
  public selectedWriterName = '';
  public selectedWriterId: number | null = null;

  constructor(
    private apisService: ApisService,
    private loading: LoadingService,
    private toastr: ToastrService,
    private language: LanguageService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.language.currentLang.subscribe(data => (this.lang = data || 'ar'));
    this.route.queryParams.subscribe(params => {
      this.selectedWriterId = params['writerId'] ? Number(params['writerId']) : null;
      this.selectedWriterName = decodeURIComponent(params['writerName'] || '');
      this.applyFilter();
    });
    this.getStories();
  }

  getStories() {
    this.loading.show();
    this.apisService.getDashboardStories().subscribe({
      next: (res: any) => {
        this.loading.hide();
        this.stories = res?.data || [];
        this.applyFilter();
      },
      error: () => {
        this.loading.hide();
        this.toastr.error(this.lang === 'ar' ? 'فشل تحميل القصص' : 'Failed to load stories');
      }
    });
  }

  onSearch(event: any) {
    this.searchValue = (event?.target?.value || '').toLowerCase();
    this.applyFilter();
  }

  applyFilter() {
    const q = this.searchValue.trim();
    this.filteredStories = this.stories.filter((story: any) => {
      const title = (story?.title || '').toLowerCase();
      const writer = (story?.writer_name || '').toLowerCase();
      const status = (story?.status || '').toLowerCase();
      const matchesSearch = !q || title.includes(q) || writer.includes(q) || status.includes(q) || String(story?.id || '').includes(q);
      const matchesWriter = !this.selectedWriterId || this.getWriterId(story) === this.selectedWriterId;
      return matchesSearch && matchesWriter;
    });
  }

  getWriterId(row: any): number | null {
    const writerId = row?.user_id || row?.writer_id || row?.user?.id || row?.writer?.id;
    return writerId ? Number(writerId) : null;
  }

  openWriterProfile(row: any) {
    const writerId = this.getWriterId(row);
    if (!writerId) {
      this.toastr.info(this.lang === 'ar' ? 'لا يوجد كاتب مرتبط بهذه القصة' : 'No writer is linked to this story');
      return;
    }

    this.router.navigate(['/edit-user', writerId]);
  }

  sendNotificationToWriter(row: any) {
    const writerId = this.getWriterId(row);
    if (!writerId) {
      this.toastr.info(this.lang === 'ar' ? 'لا يوجد كاتب مرتبط بهذه القصة' : 'No writer is linked to this story');
      return;
    }

    this.router.navigate(['/send-notification', writerId]);
  }

  getStatusLabel(status: string): string {
    const key = (status || 'pending').toLowerCase();
    const mapAr: any = { draft: 'مسودة', pending: 'بانتظار المراجعة', published: 'منشورة', rejected: 'مرفوضة', archived: 'مؤرشفة' };
    const mapEn: any = { draft: 'Draft', pending: 'Pending review', published: 'Published', rejected: 'Rejected', archived: 'Archived' };
    return this.lang === 'ar' ? (mapAr[key] || key) : (mapEn[key] || key);
  }

  handleImageError(event: any) {
    event.target.src = 'assets/images/icons/logo.jpg';
  }

  openStory(row: any) {
    if (!row?.entry_id) {
      this.toastr.info(this.lang === 'ar' ? 'القصة غير مرتبطة بمشاركة مسابقة حتى الآن' : 'This story is not linked to a contest entry yet');
      return;
    }
    this.router.navigate(['/contests/entries', row.entry_id]);
  }

  approveStory(row: any) {
    if (!row?.id) return;
    this.changeStatus(row, 'published');
  }

  rejectStory(row: any) {
    if (!row?.id) return;
    const reason = window.prompt(this.lang === 'ar' ? 'اكتب سبب الرفض (اختياري):' : 'Enter rejection reason (optional):') || '';
    this.changeStatus(row, 'rejected', reason);
  }


  editStory(row: any) {
    if (!row?.id) return;

    const newTitle = window.prompt(this.lang === 'ar' ? 'عنوان القصة:' : 'Story title:', row.title || '') ?? '';
    if (newTitle.trim() === '') return;

    const newContent = window.prompt(this.lang === 'ar' ? 'محتوى القصة:' : 'Story content:', row.content || '') ?? '';
    if (newContent.trim() === '') return;

    this.apisService.updateDashboardStory(row.id, { title: newTitle, content: newContent }).subscribe({
      next: () => {
        this.toastr.success(this.lang === 'ar' ? 'تم تعديل القصة' : 'Story updated');
        this.getStories();
      },
      error: () => this.toastr.error(this.lang === 'ar' ? 'فشل تعديل القصة' : 'Failed to update story')
    });
  }

  changeStatus(row: any, status: string, reason: string = '') {
    if (!row?.id || !status || row.status === status) return;
    this.apisService.updateDashboardStoryStatus(row.id, status, reason).subscribe({
      next: () => {
        this.toastr.success(this.lang === 'ar' ? 'تم تحديث حالة القصة' : 'Story status updated');
        this.getStories();
      },
      error: () => this.toastr.error(this.lang === 'ar' ? 'فشل تحديث حالة القصة' : 'Failed to update story status')
    });
  }
}
